import { useState } from "react";
import { Share2, Download, Link2, Loader2, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePreparedFile, downloadFile, fetchShareLink, isOfficeFile, FILE_SHARE_UNSUPPORTED, OFFICE_SHARE_BLOCKED } from "@/lib/share";
import { formatBytes } from "@/lib/format";

export function ShareFileDialog({ att, open, onClose, title }) {
  const { file, loading, error, shareable } = usePreparedFile(att, open);
  const [linkBusy, setLinkBusy] = useState(false);

  const shareNow = async () => {
    try { await navigator.share({ files: [file], title: title || att.filename }); onClose(); }
    catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.name === "NotAllowedError" ? "O navegador bloqueou o compartilhamento. Toque em Compartilhar novamente." : "Não foi possível compartilhar o arquivo.");
    }
  };
  const shareLink = async () => {
    setLinkBusy(true);
    try {
      const url = await fetchShareLink(att);
      const text = `${title ? title + " — " : ""}${att.filename}\n${url}\n(link válido por 24 horas)`;
      if (navigator.share) { await navigator.share({ title: att.filename, text }); onClose(); return; }
      await navigator.clipboard?.writeText(text);
      toast.success("Link copiado. Cole no app desejado.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível gerar o link."); }
    finally { setLinkBusy(false); }
  };

  if (!att) return null;
  const blocked = !loading && file && !shareable;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm overflow-x-hidden rounded-3xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="share-file-dialog">
        <DialogTitle>Compartilhar arquivo</DialogTitle>
        <DialogDescription className="sr-only">Enviar o arquivo anexado para outro aplicativo</DialogDescription>
        <div className="ga-surface flex min-w-0 items-center gap-3 rounded-2xl p-3">
          <FileText size={22} className="shrink-0" style={{ color: "#0A6373" }} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium" title={att.filename} data-testid="share-file-name">{att.filename}</div>
            <div className="ga-muted text-xs">{att.size ? formatBytes(att.size) : att.content_type}</div>
          </div>
        </div>
        {loading && <div className="flex items-center gap-2 text-sm" data-testid="share-file-loading"><Loader2 size={16} className="animate-spin" /> Preparando arquivo...</div>}
        {error && <p className="text-sm text-red-500" data-testid="share-file-error">{error}</p>}
        {shareable && (
          <>
            <button onClick={shareNow} className="ga-btn-primary w-full" data-testid="share-file-native"><Share2 size={18} /> Compartilhar arquivo</button>
            <p className="ga-muted break-words text-center text-xs">O arquivo real será anexado no app que você escolher (WhatsApp, Telegram, Gmail, Drive…).</p>
          </>
        )}
        {blocked && (
          <div className="min-w-0 rounded-2xl p-3 text-sm" style={{ background: "#FFF4E5", color: "#7A4A00" }} data-testid="share-file-unsupported">
            <p className="break-words">{isOfficeFile(att.filename) ? OFFICE_SHARE_BLOCKED : FILE_SHARE_UNSUPPORTED}</p>
          </div>
        )}
        {file && (
          <div className="space-y-2">
            <button onClick={() => downloadFile(file, att.filename)} className={`${blocked ? "ga-btn-primary" : "ga-btn-ghost"} w-full`} data-testid="share-file-download"><Download size={18} /> Baixar arquivo</button>
            <button onClick={shareLink} disabled={linkBusy} className="ga-btn-ghost w-full" data-testid="share-file-link">{linkBusy ? <Loader2 size={18} className="animate-spin" /> : navigator.share ? <Link2 size={18} /> : <Copy size={18} />} Enviar link para baixar (24 h)</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
